import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import type { StateCategory } from '@/types/domain'
import type { StalledProject } from '@/types/planning'

const CATEGORY_CLASS: Record<StateCategory, string> = {
  backlog: 'text-ink-500 border-line',
  started: 'text-brand-600 border-brand-300 bg-brand-50',
  blocked: 'text-[color:var(--color-state-blocked)] border-[color:var(--color-state-blocked)]',
  done: 'text-[color:var(--color-state-done)] border-[color:var(--color-state-done)]',
  cancelled: 'text-ink-500 border-line',
}

/**
 * Every stalled engagement in the group, worst first.
 *
 * The number in the last column is working days on the Oman calendar, computed
 * server-side — a figure a client would accept, not a subtraction of two dates.
 */
export function StalledTable({ rows }: { rows: StalledProject[] }) {
  const { t } = useTranslation()

  if (rows.length === 0) {
    return (
      <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
        {t('portfolio.nothingStalled')}
      </p>
    )
  }

  return (
    <div className="border-line bg-surface overflow-x-auto rounded-md border">
      <table className="w-full min-w-[780px] text-sm">
        <thead>
          <tr className="border-line-strong text-ink-500 border-b font-mono text-[10px] tracking-widest uppercase">
            <th className="px-4 py-2.5 text-start font-medium">{t('portfolio.project')}</th>
            <th className="px-4 py-2.5 text-start font-medium">{t('portfolio.division')}</th>
            <th className="px-4 py-2.5 text-start font-medium">{t('portfolio.clients')}</th>
            <th className="px-4 py-2.5 text-start font-medium">{t('portfolio.stage')}</th>
            <th className="px-4 py-2.5 text-start font-medium">{t('portfolio.lead')}</th>
            <th className="px-4 py-2.5 text-end font-medium">{t('portfolio.openItems')}</th>
            <th className="px-4 py-2.5 text-end font-medium">{t('portfolio.daysStalled')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((project) => (
            <tr key={project.id} className="border-line border-b last:border-0">
              <td className="px-4 py-2.5">
                <Link
                  to={`/projects/${project.key}`}
                  className="text-brand-600 font-mono text-xs hover:underline"
                >
                  {project.key}
                </Link>
                <div className="text-ink-900 mt-0.5">{project.name}</div>
              </td>
              <td className="text-ink-700 px-4 py-2.5">{project.divisionKey ?? '—'}</td>
              <td className="text-ink-700 px-4 py-2.5">{project.client ?? '—'}</td>
              <td className="px-4 py-2.5">
                {project.stage === null ? (
                  <span className="text-ink-500">—</span>
                ) : (
                  <span
                    className={`rounded border px-2 py-0.5 font-mono text-xs ${
                      CATEGORY_CLASS[project.stageCategory ?? 'backlog']
                    }`}
                  >
                    {project.stage}
                  </span>
                )}
              </td>
              <td className="text-ink-700 px-4 py-2.5">{project.lead ?? '—'}</td>
              <td className="text-ink-700 px-4 py-2.5 text-end tabular-nums">
                {project.openWorkItems}
              </td>
              <td className="px-4 py-2.5 text-end">
                <span className="font-mono text-sm font-semibold tabular-nums text-[color:var(--color-state-blocked)]">
                  {project.daysStalled}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
